import '@/lib/polyfills';
import '@/lib/axios';
import '@/lib/dayjs';
import '@fontsource-variable/inter';
import '@mantine/core/styles.css';
import '@mantine/carousel/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import '@mantine/spotlight/styles.css';
import '@/styles/markdown.css';

import { createInertiaApp, type ResolvedComponent } from '@inertiajs/react';
import { MantineProvider } from '@mantine/core';
import { DatesProvider } from '@mantine/dates';
import { QueryClientProvider } from '@tanstack/react-query';
import { type ReactNode, StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import DefaultLayout from '@/components/layout/DefaultLayout';
import { initI18n } from '@/lib/i18n';
import { queryClient } from '@/lib/queryClient';
import { initSentry } from '@/lib/sentry';
import { theme } from '@/lib/theme';
import type { Locale } from '@/types';

const pages = import.meta.glob<{ default: ResolvedComponent }>('../pages/**/*.tsx');

window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault();
  window.location.reload();
});

// Initialize error tracking before the app boots so startup errors are captured.
initSentry();

void createInertiaApp({
  title: (title) => (title ? `${title} - Kali` : 'Kali'),

  resolve: async (name) => {
    const loadPage = pages[`../pages/${name}.tsx`];

    if (!loadPage) {
      // biome-ignore lint/suspicious/noConsole: Report missing Inertia pages during boot.
      console.error(`Missing Inertia page component: '${name}.tsx'`);
    }

    const page = await loadPage();

    // Set default layout for all pages
    page.default.layout ||= (page: ReactNode) => <DefaultLayout>{page}</DefaultLayout>;

    return page;
  },

  setup({ el, App, props }) {
    const locale = props.initialPage.props.locale as unknown as Locale;
    initI18n(locale);

    createRoot(el).render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <MantineProvider theme={theme}>
            <DatesProvider settings={{ locale }}>
              <App {...props} />
            </DatesProvider>
          </MantineProvider>
        </QueryClientProvider>
      </StrictMode>,
    );
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: true,
    },
    future: {
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById('app')) {
    throw error;
  } else {
    // biome-ignore lint/suspicious/noConsole: Report Inertia mounting errors during boot.
    console.error(
      'Missing root element.\n\n' +
        'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
        'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
    );
  }
});

// Register service worker for push notifications
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/service-worker').catch((error) => {
    // biome-ignore lint/suspicious/noConsole: Report service worker registration failures.
    console.error('Service Worker registration failed:', error);
  });
}
